import { Component, Inject, NgZone, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { OKTA_AUTH } from '@okta/okta-angular';
import { OktaAuth } from '@okta/okta-auth-js';
import { OktaSignIn } from '@okta/okta-signin-widget';
import { firstValueFrom } from 'rxjs';
import { AuthService } from 'src/app/core/services/auth.service';
import { EnvironmentService } from 'src/app/core/services/environment.service';
import { SsoConfig } from 'th-env';
import { AccountUnlockComponent } from '../account-unlock/account-unlock.component';
import { AppNotificationComponent } from '../app-notification/app-notification.component';

const DEFAULT_ORIGINAL_URI = window.location.origin;

// Okta error code for credential failures (wrong password, unknown username).
// Stable across widget versions — see https://developer.okta.com/docs/reference/error-codes/#E0000004
const OKTA_AUTH_FAILURE_CODE = 'E0000004';

@Component({
  selector: 'th-login-sso',
  templateUrl: './login-sso.component.html',
  styleUrls: ['./login-sso.component.scss'],
  imports: [AppNotificationComponent, AccountUnlockComponent]
})
export class LoginSsoComponent implements OnInit, OnDestroy {
  signIn: OktaSignIn;

  get ssoConfig(): SsoConfig {
    return this.environmentService.ssoConfig;
  }

  get failedLoginUnlockThreshold(): number {
    return this.environmentService.failedLoginUnlockThreshold;
  }

  get supportPhone(): string {
    return this.environmentService.supportPhone;
  }

  loading = false;
  widgetError = false;
  isUnlockMode = false;
  selfServiceUnlockEnabled = false;
  lockedOutUsername = '';
  failedLoginAttempts = 0;

  /**
   * Tracks the username as the user types into the Okta widget so it can be pre-populated
   * when the unlock flow is triggered. The Okta Sign-In Widget (v7.x) does not expose form
   * field values via its event API, so we attach a DOM input listener in afterRender as a
   * workaround. If product removes the requirement to auto-populate the username on the unlock
   * screen, this field and the associated listener can be removed.
   */
  private _trackedUsername = '';
  private _lastCountedUsername = '';
  private _usernameInput: HTMLInputElement | null = null;
  private _usernameInputHandler: (() => void) | null = null;

  constructor(
    @Inject(OKTA_AUTH) private oktaAuth: OktaAuth,
    private environmentService: EnvironmentService,
    private authService: AuthService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private ngZone: NgZone
  ) {}

  async ngOnInit() {
    const skipLegacyRedirect = this.activatedRoute.snapshot.queryParamMap.get('skipLegacyRedirect') === 'true';

    if (this.environmentService.loginUrl === '/login-legacy' && !skipLegacyRedirect) {
      this.router.navigate([this.environmentService.loginUrl]);
    } else {
      this.loading = true;

      // Fetch feature flag before initializing the widget so selfServiceUnlock
      // config is correct on first render. Defaults to false on error.
      try {
        this.selfServiceUnlockEnabled = await firstValueFrom(this.authService.isSelfServiceUnlockEnabled());
      } catch (err) {
        console.warn('Failed to fetch self-service-account-unlock flag; defaulting to false', err);
        this.selfServiceUnlockEnabled = false;
      }

      try {
        await this.initializeLoginForm();
      } catch {
        this.widgetError = true;
      } finally {
        this.loading = false;
      }
    }
  }

  ngOnDestroy() {
    this._cleanupUsernameListener();
    this.signIn?.off('afterError');
    this.signIn?.off('afterRender');
    this.signIn?.remove();
  }

  private _cleanupUsernameListener() {
    if (this._usernameInput && this._usernameInputHandler) {
      this._usernameInput.removeEventListener('input', this._usernameInputHandler);
      this._usernameInput.removeEventListener('change', this._usernameInputHandler);
    }
    this._usernameInput = null;
    this._usernameInputHandler = null;
  }

  goToUnlock() {
    if (!this.selfServiceUnlockEnabled) return;
    this.lockedOutUsername = this._trackedUsername;
    this.isUnlockMode = true;
  }

  async backToLogin() {
    if (this.loading) return;
    this.isUnlockMode = false;
    this.widgetError = false;
    this.lockedOutUsername = '';
    this.failedLoginAttempts = 0;
    this._trackedUsername = '';
    this._lastCountedUsername = '';
    this._cleanupUsernameListener();
    this.signIn?.off('afterError');
    this.signIn?.off('afterRender');
    this.signIn?.remove();
    this.loading = true;
    try {
      await this.initializeLoginForm();
    } catch {
      this.widgetError = true;
    } finally {
      this.loading = false;
    }
  }

  async initializeLoginForm() {
    this.widgetError = false;
    this.signIn = new OktaSignIn({
      /**
       * Note: when using the Sign-In Widget for an OIDC flow, it still
       * needs to be configured with the base URL for your Okta Org. Here
       * we derive it from the given issuer for convenience.
       */
      baseUrl: this.ssoConfig.oidc.issuer.split('/oauth2')[0],
      clientId: this.ssoConfig.oidc.clientId,
      redirectUri: this.ssoConfig.oidc.redirectUri,
      logo: `assets/img/logo/${this.environmentService.logo}`,
      helpLinks: {
        custom: [
          {
            href: '#',
            text: `For assistance logging in, please call our support team - ${this.environmentService.supportPhone}`
          }
        ]
      },
      i18n: {
        en: {
          'primaryauth.title': 'Sign in',
          'password.forgot.email.or.username.placeholder': 'Username',
          'password.forgot.email.or.username.tooltip': 'Username',
          'error.auth.lockedOut':
            "For your security, your account has been locked after multiple failed login attempts. We've sent you an email with instructions to unlock your account."
        }
      },
      // TODO: migrate to OIE — requires updating afterError error shape detection
      // (OIE uses errorSummaryKeys instead of errorCode) and revisiting username
      // tracking since OIE uses a two-step identifier-then-password flow.
      useClassicEngine: true,
      features: {
        rememberMe: false,
        // When our custom unlock flow is active (flag ON), suppress the native widget link.
        // When flag is OFF, restore native Okta behavior.
        selfServiceUnlock: !this.selfServiceUnlockEnabled
      }
    });

    const originalUri = this.oktaAuth.getOriginalUri();
    if (!originalUri || originalUri === DEFAULT_ORIGINAL_URI) {
      this.oktaAuth.setOriginalUri('/private');
    }

    try {
      const response = await this.oktaAuth.token.getWithoutPrompt();
      await this.oktaAuth.handleLoginRedirect(response.tokens);
    } catch (error) {
      console.debug('[LoginSso] SSO pre-check failed; rendering widget', (error as Error)?.message);
      this.authService.loginResponse = null;
      this.signIn.on('afterRender', () => {
        this._cleanupUsernameListener();
        const widgetEl = document.querySelector('#sign-in-widget');
        const usernameInput = widgetEl?.querySelector<HTMLInputElement>('#okta-signin-username, [name="identifier"]');
        if (usernameInput) {
          this._usernameInput = usernameInput;
          this._usernameInputHandler = () => {
            this._trackedUsername = usernameInput.value.trim();
          };
          usernameInput.addEventListener('input', this._usernameInputHandler);
          usernameInput.addEventListener('change', this._usernameInputHandler);
        }
      });
      this.signIn.on('afterError', (_context: unknown, error: unknown) => {
        if (!this.selfServiceUnlockEnabled) {
          return;
        }

        // We count auth failures rather than reacting to a distinct "account locked" error
        // because Okta Classic Engine returns E0000004 on every failed attempt — including
        // all attempts after the account has been locked. There is no separate error code
        // for the locked state, so we mirror the policy threshold configured in Okta and
        // redirect proactively when the count is reached.
        const err = error as { xhr?: { responseJSON?: { errorCode?: string } } };
        const isAuthFailure = err.xhr?.responseJSON?.errorCode === OKTA_AUTH_FAILURE_CODE;
        if (!isAuthFailure) {
          return;
        }

        // Fallback: read current value directly if listener missed a paste/autofill event
        if (!this._trackedUsername && this._usernameInput) {
          this._trackedUsername = this._usernameInput.value.trim();
        }

        if (!this._trackedUsername) {
          return;
        }

        this.ngZone.run(() => {
          // Reset the counter when the username changes between attempts (shared workstation case).
          if (this._trackedUsername !== this._lastCountedUsername) {
            this.failedLoginAttempts = 0;
            this._lastCountedUsername = this._trackedUsername;
          }
          this.failedLoginAttempts++;
          // This counter is a UX aid to surface the unlock flow proactively.
          // The real lockout is enforced server-side by Okta — do not treat this as a security control.
          if (this.failedLoginAttempts >= this.failedLoginUnlockThreshold) {
            this.lockedOutUsername = this._trackedUsername;
            this.isUnlockMode = true;
          }
        });
      });
      this.signIn
        .showSignInAndRedirect({
          el: '#sign-in-widget',
          scopes: this.ssoConfig.oidc.scopes
        })
        .catch((err) => {
          console.error('Failed to initialize sign-in widget', err);
          this.ngZone.run(() => {
            this.widgetError = true;
          });
        });
    }
  }
}
